import defaultTo from 'lodash/defaultTo';

import type {
  CalendarGridColDef,
  CalendarGridContext,
  CalendarGridFilterTreeListPathGetter,
  CalendarGridKeyCreatorParams,
  CalendarGridSetFilterParams,
  CalendarGridSetFilterValuesFunc,
  CalendarGridValueFormatterFunc,
} from '../../CalendarGrid.model';
import type { Value } from './ExchangeCountryCellRenderer';
import { ExchangeCountryCellRenderer } from './ExchangeCountryCellRenderer';

export type SetFilterValue = [string, string];

const DEFAULT_REGION_NAME = 'Other';

export const valueFormatter: CalendarGridValueFormatterFunc<Value> = ({ data }) => {
  return data?.exchangeCountryDisplayName ?? '-';
};

export const setFilterValuesFunc: CalendarGridSetFilterValuesFunc<SetFilterValue> = params => {
  const context = params.context as CalendarGridContext;
  params.success(
    context.countries.map(({ region, label }) => [
      region?.displayName ?? DEFAULT_REGION_NAME,
      label,
    ])
  );
};

export const setFilterKeyCreator = ({
  data,
  ...params
}: CalendarGridKeyCreatorParams<SetFilterValue>) => {
  const value = data
    ? [
        defaultTo(data?.exchangeRegionDisplayName, DEFAULT_REGION_NAME),
        data?.exchangeCountryDisplayName,
      ]
    : params.value;

  return value?.join('-') ?? '';
};

export const setFilterTreeListPathGetter: CalendarGridFilterTreeListPathGetter<
  SetFilterValue
> = value => value;

export const setFilterComparator = (lhs: SetFilterValue | null, rhs: SetFilterValue | null) => {
  const lhsValue = lhs?.join('-') ?? '';
  const rhsValue = rhs?.join('-') ?? '';

  return lhsValue.localeCompare(rhsValue);
};

const exchangeCountryColDef: CalendarGridColDef<Value> = {
  field: 'exchangeCountry',
  headerName: 'Country ⓘ',
  headerTooltip: 'Exchange Country',
  width: 126,
  type: 'text',
  filter: 'agSetColumnFilter',
  filterParams: {
    treeList: true,
    values: setFilterValuesFunc,
    keyCreator: setFilterKeyCreator,
    treeListPathGetter: setFilterTreeListPathGetter,
    comparator: setFilterComparator,
  } satisfies CalendarGridSetFilterParams<SetFilterValue>,
  cellRenderer: ExchangeCountryCellRenderer,
  valueFormatter,
};

export default exchangeCountryColDef;
